/**
 * @engine/vfx - WGSL Emitter
 *
 * WGSL generation for the parts of the VFX stack that are still WGSL:
 * - render shader header (Particle struct + render uniforms) — see renderShaders.ts
 * - the static spawn / prepare-indirect compute shaders (runtime-generated,
 *   consumed by VFXSystemRenderer in editor AND player, so they stay WGSL)
 *
 * The graph compute stages (init/update) are generated as Slang by the
 * function-per-node codegen (see ./codegen and ./stages) and compiled to WGSL
 * through the injected Slang compile service.
 */

import type { VFXDataType, ParticleAttributes } from '../types';
import { DEFAULT_PARTICLE_ATTRIBUTES } from '../types';
import { VFX_BINDINGS } from '../VFXBindings';
import {
  UNIFORMS_LAYOUT,
  COUNTERS_LAYOUT,
  GPU_EVENT_LAYOUT,
  EVENT_COUNT_LAYOUT,
  SPAWN_UNIFORMS_LAYOUT,
  VFX_RENDER_UNIFORMS_LAYOUT,
} from './Layouts';

// ============================================================================
// TYPE MAPPING
// ============================================================================

/** Map VFX types to WGSL types (render-side structs). */
const WGSL_TYPES: Partial<Record<VFXDataType, string>> = {
  bool: 'bool',
  int: 'i32',
  uint: 'u32',
  float: 'f32',
  vec2: 'vec2<f32>',
  vec3: 'vec3<f32>',
  vec4: 'vec4<f32>',
  color: 'vec4<f32>',
  mat3: 'mat3x3<f32>',
  mat4: 'mat4x4<f32>',
};

// ============================================================================
// STRUCT GENERATION
// ============================================================================

/**
 * Generate the Particle struct (WGSL). Field order/types must match the Slang
 * Particle struct emitted for the compute stages — Slang std430 and WGSL
 * storage layout agree for these field types, so the buffers are byte-identical.
 */
export function generateParticleStruct(attributes?: ParticleAttributes): string {
  const attrs = attributes ?? DEFAULT_PARTICLE_ATTRIBUTES;
  const fields: string[] = [];

  for (const [name, type] of Object.entries(attrs)) {
    if (type) {
      const wgslType = WGSL_TYPES[type as VFXDataType] || 'f32';
      fields.push(`  ${name}: ${wgslType},`);
    }
  }

  // Always add alive flag
  fields.push('  alive: u32,');

  return `struct Particle {
${fields.join('\n')}
}`;
}

/**
 * Generate GPU spawn compute shader.
 * This shader runs once per frame (1 workgroup, 1 thread) and calculates how many particles to spawn.
 * Uses fixed-point accumulator stored in counters buffer for frame-independent spawning.
 */
export function generateSpawnShader(): string {
  // Generate structs from layouts (spawn shader uses simplified Uniforms)
  const uniformsStruct = SPAWN_UNIFORMS_LAYOUT.toWGSL().replace('SpawnUniforms', 'Uniforms');
  const countersStruct = COUNTERS_LAYOUT.toWGSL();

  return `// GPU Spawn Compute Shader
// Calculates spawn count based on rate and delta time

${uniformsStruct}

${countersStruct}

// VFX Bindings (see VFXBindings.ts)
@group(0) @binding(${VFX_BINDINGS.UNIFORMS}) var<uniform> uniforms: Uniforms;
@group(0) @binding(${VFX_BINDINGS.COUNTERS}) var<storage, read_write> counters: Counters;

// Fixed-point 16.16 helpers
fn toFixed16(f: f32) -> u32 {
  return u32(clamp(f, 0.0, 65535.0) * 65536.0);
}

fn fromFixed16(u: u32) -> f32 {
  return f32(u) / 65536.0;
}

@compute @workgroup_size(1)
fn main() {
  // Skip if no spawn rate
  if (uniforms.spawnRate <= 0.0) {
    atomicStore(&counters.emitCount, 0u);
    return;
  }

  // Read current accumulator (fixed-point 16.16)
  let accuFixed = atomicLoad(&counters.spawnAccumulator);
  let accumulator = fromFixed16(accuFixed);

  // Add rate * deltaTime
  let newAccumulator = accumulator + uniforms.spawnRate * uniforms.deltaTime;

  // Calculate how many whole particles to spawn
  let toSpawn = u32(floor(newAccumulator));

  // Store fractional part back (as fixed-point)
  let fractional = newAccumulator - f32(toSpawn);
  atomicStore(&counters.spawnAccumulator, toFixed16(fractional));

  // Store spawn count for init shader
  atomicStore(&counters.emitCount, toSpawn);
}
`;
}

/**
 * Generate prepare indirect compute shader for event-driven systems.
 * This shader reads events emitted by parent systems and calculates total spawn count.
 * @param spawnPerEvent - Default particles to spawn per event (used as fallback)
 */
export function generatePrepareIndirectShader(spawnPerEvent: number = 10): string {
  // Generate structs from layouts
  const uniformsStruct = UNIFORMS_LAYOUT.toWGSL();
  const countersStruct = COUNTERS_LAYOUT.toWGSL();
  const eventCountStruct = EVENT_COUNT_LAYOUT.toWGSL();
  const gpuEventStruct = GPU_EVENT_LAYOUT.toWGSL();
  const particleStruct = generateParticleStruct();

  // VFXVariables uses arrays, generate manually
  const vfxVariablesStruct = `struct VFXVariables {
  floats: array<vec4<f32>, 4>,
  vec2s: array<vec4<f32>, 4>,
  vec3s: array<vec4<f32>, 8>,
  colors: array<vec4<f32>, 8>,
}`;

  return `// Prepare Indirect Compute Shader
// Reads events from parent system and calculates spawn count for event-driven spawn

${uniformsStruct}

${countersStruct}

${eventCountStruct}

${gpuEventStruct}

${particleStruct}

${vfxVariablesStruct}

@group(0) @binding(${VFX_BINDINGS.UNIFORMS}) var<uniform> uniforms: Uniforms;
@group(0) @binding(${VFX_BINDINGS.PARTICLES}) var<storage, read_write> particles: array<Particle>;
@group(0) @binding(${VFX_BINDINGS.COUNTERS}) var<storage, read_write> counters: Counters;
@group(0) @binding(${VFX_BINDINGS.EVENTS}) var<storage, read_write> events: array<GPUEvent>;
@group(0) @binding(${VFX_BINDINGS.VFX_VARIABLES}) var<uniform> vfxVariables: VFXVariables;
@group(0) @binding(${VFX_BINDINGS.EVENT_COUNT}) var<storage, read_write> eventCount: EventCount;

@compute @workgroup_size(1)
fn main() {
  // Read event count and sum up total spawn count from all events
  let evtCount = atomicLoad(&eventCount.count);
  var totalSpawn: u32 = 0u;
  for (var i: u32 = 0u; i < min(evtCount, 1024u); i++) {
    totalSpawn += events[i].spawnCount;
  }

  // Fallback: if events don't have spawnCount, use default
  if (totalSpawn == 0u && evtCount > 0u) {
    totalSpawn = evtCount * ${spawnPerEvent}u;
  }

  // Store spawn count in emitCount for init shader to read
  atomicStore(&counters.emitCount, totalSpawn);
}
`;
}

/**
 * Generate uniform bindings for render shaders.
 * Uses VFX_RENDER_UNIFORMS_LAYOUT from StructLayout.ts.
 */
export function generateRenderUniformBindings(): string {
  const uniformsStruct = VFX_RENDER_UNIFORMS_LAYOUT.toWGSL();
  return `${uniformsStruct}

@group(0) @binding(${VFX_BINDINGS.UNIFORMS}) var<uniform> vfxUniforms: VFXUniforms;`;
}

// ============================================================================
// WGSL BUILDER
// ============================================================================

/**
 * Builder for constructing WGSL shader code.
 */
export class WGSLBuilder {
  private _lines: string[] = [];
  private currentIndent = 0;

  /**
   * Add a line of code.
   */
  line(code: string): this {
    const indent = '  '.repeat(this.currentIndent);
    this._lines.push(indent + code);
    return this;
  }

  /**
   * Add multiple lines.
   */
  addLines(code: string): this {
    const indent = '  '.repeat(this.currentIndent);
    for (const line of code.split('\n')) {
      this._lines.push(indent + line);
    }
    return this;
  }

  /**
   * Add a blank line.
   */
  blank(): this {
    this._lines.push('');
    return this;
  }

  /**
   * Add a comment.
   */
  comment(text: string): this {
    return this.line(`// ${text}`);
  }

  /**
   * Increase indent level.
   */
  indent(): this {
    this.currentIndent++;
    return this;
  }

  /**
   * Decrease indent level.
   */
  dedent(): this {
    this.currentIndent = Math.max(0, this.currentIndent - 1);
    return this;
  }

  /**
   * Start a block (with opening brace).
   */
  block(header: string): this {
    this.line(header + ' {');
    this.indent();
    return this;
  }

  /**
   * End a block (with closing brace).
   */
  endBlock(): this {
    this.dedent();
    this.line('}');
    return this;
  }

  /**
   * Add raw code (no indent processing).
   */
  raw(code: string): this {
    this._lines.push(code);
    return this;
  }

  /**
   * Build the final shader string.
   */
  build(): string {
    return this._lines.join('\n');
  }
}
